import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Auto-return-to-idle timer for the kiosk.
 *
 * Starts a countdown that calls `onIdle` when it expires. The timer
 * is automatically reset whenever the user interacts with the device
 * (touch, mouse, keyboard). It can be paused during API calls and
 * resumed afterwards.
 *
 * @param {number}   timeoutSeconds  Seconds of inactivity before onIdle fires. 0 or null disables the timer.
 * @param {Function} onIdle          Callback invoked when the timer expires.
 */
export function useKioskIdleTimer(timeoutSeconds = 30, onIdle) {
  const [remainingSeconds, setRemainingSeconds] = useState(timeoutSeconds || 0);
  const [isActive, setIsActive] = useState(false);

  // Refs to hold mutable state that shouldn't trigger re-renders.
  const countdownRef = useRef(null); // setInterval id for the 1-second tick
  const onIdleRef = useRef(onIdle);
  const timeoutRef = useRef(timeoutSeconds);
  const isPausedRef = useRef(false);
  const remainingOnPauseRef = useRef(0);

  // Keep refs up to date so callbacks never go stale.
  onIdleRef.current = onIdle;
  timeoutRef.current = timeoutSeconds;

  // ---------------------------------------------------------------
  // Internal: stop the interval
  // ---------------------------------------------------------------
  const stopCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setIsActive(false);
  }, []);

  // ---------------------------------------------------------------
  // Internal: start the interval from a given number of seconds
  // ---------------------------------------------------------------
  const startCountdown = useCallback(
    (seconds) => {
      stopCountdown();

      if (!seconds || seconds <= 0) return;

      let remaining = seconds;
      setRemainingSeconds(remaining);
      setIsActive(true);

      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setRemainingSeconds(remaining);

        if (remaining <= 0) {
          stopCountdown();
          setRemainingSeconds(0);
          onIdleRef.current?.();
        }
      }, 1000);
    },
    [stopCountdown],
  );

  // ---------------------------------------------------------------
  // Public: reset the timer back to the full duration
  // ---------------------------------------------------------------
  const reset = useCallback(() => {
    isPausedRef.current = false;
    startCountdown(timeoutRef.current);
  }, [startCountdown]);

  // ---------------------------------------------------------------
  // Public: pause the timer (e.g., during API calls)
  // ---------------------------------------------------------------
  const pause = useCallback(() => {
    if (isPausedRef.current) return;
    isPausedRef.current = true;
    // Capture the remaining seconds at the moment of pause.
    remainingOnPauseRef.current = remainingSeconds;
    stopCountdown();
  }, [remainingSeconds, stopCountdown]);

  // ---------------------------------------------------------------
  // Public: resume the timer from where it was paused
  // ---------------------------------------------------------------
  const resume = useCallback(() => {
    if (!isPausedRef.current) return;
    isPausedRef.current = false;
    startCountdown(remainingOnPauseRef.current);
  }, [startCountdown]);

  // ---------------------------------------------------------------
  // Document-level interaction listeners to reset the timer
  // ---------------------------------------------------------------
  useEffect(() => {
    const disabled = !timeoutSeconds || timeoutSeconds <= 0;
    if (disabled) {
      stopCountdown();
      return;
    }

    const handleInteraction = () => {
      if (isPausedRef.current) return;
      reset();
    };

    const events = ['touchstart', 'mousedown', 'keydown'];
    events.forEach((evt) => document.addEventListener(evt, handleInteraction, { passive: true }));

    // Kick off the timer on mount / when timeout changes.
    startCountdown(timeoutSeconds);

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, handleInteraction));
      stopCountdown();
    };
    // We intentionally omit `reset` from deps -- `startCountdown` and
    // `stopCountdown` are stable (useCallback with no changing deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutSeconds, startCountdown, stopCountdown]);

  return {
    reset,
    pause,
    resume,
    remainingSeconds,
    isActive,
  };
}
